/* NotificationContext — Tenant Admin Notification Foundation v1.
   Delivery v1: polling ringan unread-count (30s, pause saat tab hidden).
   WebSocket/SSE = Phase 2. Sound (WebAudio) + Browser Notification saat tab aktif. */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { imageUrl } from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

const Ctx = createContext(null);
const LAST_SEEN_KEY = "dynicty_notif_last_seen";
const POLL_MS = 30000;

const playChime = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    if (ctx.state === "suspended") { ctx.close(); return; } // autoplay diblokir → diam
    const t = ctx.currentTime;
    [[880, t, 0.12], [1318.5, t + 0.12, 0.18]].forEach(([freq, start, dur]) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.05, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(start); o.stop(start + dur + 0.02);
    });
    setTimeout(() => ctx.close(), 800);
  } catch { /* fallback diam */ }
};

export function NotificationProvider({ children }) {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const inAppFeature = useFeatureFlag("notifications.in_app");
  const [unreadCount, setUnreadCount] = useState(0);
  const [prefs, setPrefs] = useState(null);
  const [entitlements, setEntitlements] = useState({ sound: false, browser: false });
  const prefsRef = useRef(null);
  const brandingRef = useRef(branding);
  brandingRef.current = branding;
  prefsRef.current = prefs;
  const enabled = inAppFeature.loading || inAppFeature.enabled; // fail-open saat flags belum termuat

  const notifyBrowser = useCallback((latest) => {
    const p = prefsRef.current;
    if (!p?.browser_notification_enabled || typeof Notification === "undefined") return;
    if (Notification.permission !== "granted" || document.visibilityState !== "visible") return;
    try {
      const b = brandingRef.current;
      const icon = b?.visual?.favicon ? imageUrl(b.visual.favicon)
        : b?.visual?.logo ? imageUrl(b.visual.logo) : "/favicon-32x32.png";
      const n = new Notification(`${latest.title} — ${b?.general?.name || "Dynicty"}`, {
        body: latest.message, icon, tag: latest.id,
      });
      n.onclick = () => { window.focus(); navigate(latest.action_url || "/admin/notifications"); n.close(); };
    } catch { /* ignore */ }
  }, [navigate]);

  const poll = useCallback(async () => {
    try {
      const r = await api.get("/admin/notifications/unread-count");
      setUnreadCount(r.data.unread_count || 0);
      const latest = r.data.latest;
      if (!latest) return;
      const lastSeen = localStorage.getItem(LAST_SEEN_KEY) || "";
      if (latest.created_at > lastSeen) {
        localStorage.setItem(LAST_SEEN_KEY, latest.created_at);
        if (lastSeen && !latest.is_read) { // bukan first-load → notifikasi BARU (sekali per notification)
          if (prefsRef.current?.sound_enabled) playChime();
          notifyBrowser(latest);
        }
      }
    } catch { /* silent */ }
  }, [notifyBrowser]);

  const loadPrefs = useCallback(async () => {
    try {
      const r = await api.get("/admin/notifications/preferences");
      setPrefs(r.data.preferences);
      setEntitlements(r.data.entitlements || {});
    } catch { /* silent */ }
  }, []);

  const savePrefs = useCallback(async (patch) => {
    const r = await api.put("/admin/notifications/preferences", patch);
    setPrefs(r.data.preferences);
    setEntitlements(r.data.entitlements || {});
    return r.data.preferences;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    loadPrefs();
    poll();
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") poll();
    }, POLL_MS);
    const onVisible = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisible); };
  }, [enabled, poll, loadPrefs]);

  const markRead = useCallback(async (id) => {
    const r = await api.put(`/admin/notifications/${id}/read`);
    setUnreadCount(r.data.unread_count ?? 0);
  }, []);

  const markAllRead = useCallback(async () => {
    await api.put("/admin/notifications/read-all");
    setUnreadCount(0);
  }, []);

  return (
    <Ctx.Provider value={{ enabled, unreadCount, setUnreadCount, prefs, entitlements, savePrefs, markRead, markAllRead, refresh: poll }}>
      {children}
    </Ctx.Provider>
  );
}

export const useNotifications = () => useContext(Ctx) || {
  enabled: false, unreadCount: 0, prefs: null, entitlements: {},
  savePrefs: async () => {}, markRead: async () => {}, markAllRead: async () => {}, refresh: async () => {},
};
